import { IClass } from '../models/class.model';
import {
  BehaviorSubject,
  combineLatest,
  firstValueFrom,
  Observable,
  of,
  switchMap,
  timer,
} from 'rxjs';
import { map } from 'rxjs/operators';
import { IClassWithCurrUserResModel } from '../models/classWithCurrUserRes.model';
import { IUserClass } from '../models/userClass.model';
import { AuthService } from './auth.service';
import {
  Firestore,
  Query,
  collection,
  doc,
  getDocs,
  query as fsQuery,
  runTransaction,
  setDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { ClassType } from '../enums/class-type';
import { CapacityBelowBookedError } from '../errors/CapacityBelowBookedError';
import { Inject, Injectable, NgZone } from '@angular/core';
import { IClassWithRegularDateModel } from '../models/classWithRegularDate.model';
import {
  collectionData$,
  docData$,
  resettableReplay,
} from '../utils/FirestoreRx';
import { FIRESTORE } from '../firebase.providers';

@Injectable({
  providedIn: 'root',
})
export class ClassService {
  private readonly recentAndUpcomingClasses: Query;

  // The listener opens on the first subscriber, replays the latest snapshot to late
  // subscribers, and closes when the last unsubscribes, so no Firestore read happens
  // until a view actually needs the class list (see resettableReplay above for the
  // error semantics).
  private classes$: Observable<IClass[]>;

  /**
   * classId → the member's own reservation count as the screen expects it right after a
   * booking or cancellation resolved. A Firestore transaction raises no local snapshot,
   * so the live listeners only show the new count after a server round-trip; on a slow
   * connection the card kept its old numbers for seconds after the toast said
   * "gereserveerd". The stream below layers the expected count (and its +1/-1 on the
   * class total) over the live data and drops it the moment the server reports the
   * same count, so the card is right instantly and still self-corrects.
   */
  private readonly ownReservationOverrides = new BehaviorSubject<
    Map<string, number>
  >(new Map());

  overrideOwnReservations(classId: string, expected: number): void {
    const next = new Map(this.ownReservationOverrides.value);
    next.set(classId, Math.max(0, expected));
    this.ownReservationOverrides.next(next);
  }

  constructor(
    @Inject(FIRESTORE) private db: Firestore,
    private authService: AuthService,
    private zone: NgZone,
  ) {
    // One week of history: the admin previous-classes view reaches 7 days back, and
    // the window bounds the listener's read cost (~7 classes/week schedule).
    const currentDate = new Date();
    currentDate.setDate(currentDate.getDate() - 7);
    this.recentAndUpcomingClasses = fsQuery(
      collection(this.db, 'classes'),
      where('dateEpoch', '>', currentDate.getTime()),
    );

    this.classes$ = collectionData$<IClass>(
      this.recentAndUpcomingClasses,
      this.zone,
    ).pipe(
      map((classes) => [...classes].sort((a, b) => a.dateEpoch - b.dateEpoch)),
      resettableReplay<IClass[]>(),
    );
  }

  async addClass(newClass: IClass): Promise<void> {
    if (await firstValueFrom(this.authService.userIsAdmin$)) {
      await setDoc(doc(this.db, 'classes', newClass.id), newClass);
    }
  }

  /**
   * Changes the type or the capacity of a class in place, keeping its bookings. The
   * date is deliberately not editable: every booking on it would move without the
   * member knowing. A transaction, because the capacity floor is the live counter: a
   * booking landing between a read and a write could otherwise leave more bikes
   * booked than the class holds.
   */
  async updateClass(
    classId: string,
    changes: { type: ClassType; maxReservations: number },
  ): Promise<void> {
    if (!(await firstValueFrom(this.authService.userIsAdmin$))) {
      return;
    }
    const classRef = doc(this.db, 'classes', classId);
    await runTransaction(this.db, async (transaction) => {
      const snapshot = await transaction.get(classRef);
      const current = snapshot.data() as IClass | undefined;
      if (!current) {
        throw new Error('Class not found');
      }
      if (changes.maxReservations < (current.currentReservations ?? 0)) {
        throw new CapacityBelowBookedError();
      }
      transaction.update(classRef, {
        type: changes.type,
        maxReservations: changes.maxReservations,
      });
    });
  }

  async removeClass(classId: string): Promise<void> {
    if (!(await firstValueFrom(this.authService.userIsAdmin$))) {
      return;
    }
    // Read only this class's attendee subcollection (≤ maxReservations docs) rather
    // than the whole users collection. Delete both reservation mirrors per attendee
    // plus the class doc in one atomic batch (attendees stay well under the 500-op limit).
    const attendees = await getDocs(
      collection(this.db, `classes/${classId}/users`),
    );
    const batch = writeBatch(this.db);
    attendees.forEach((attendee) => {
      // The mirror doc id IS the userId (path classes/{id}/users/{userId}).
      const userId = attendee.id;
      batch.delete(doc(this.db, `classes/${classId}/users/${userId}`));
      batch.delete(doc(this.db, `users/${userId}/classes/${classId}`));
    });
    batch.delete(doc(this.db, 'classes', classId));
    await batch.commit();
  }

  getClassById(classId: string): Observable<IClass | null> {
    return docData$<IClass>(doc(this.db, 'classes', classId), this.zone).pipe(
      map((classData) => classData ?? null),
    );
  }

  getClasses(): Observable<IClass[]> {
    return this.classes$.pipe(
      map((classes) =>
        classes.filter((iClass) => iClass.dateEpoch > Date.now()),
      ),
    );
  }

  getClassesWithPreviousClasses(): Observable<IClassWithRegularDateModel[]> {
    return this.classes$.pipe(
      map((classes) =>
        classes.map((iClass) => ({
          ...iClass,
          date: new Date(iClass.dateEpoch),
        })),
      ),
    );
  }

  getClassesWithReservationsForCurrentUser(): Observable<
    IClassWithCurrUserResModel[]
  > {
    // getClasses() sits OUTSIDE the switchMap on purpose. user$ is a BehaviorSubject
    // that emits null before the signed-in user arrives, so with the class listener
    // inside, the first emission subscribed it and the second tore it down: its
    // refcount hit zero, resettableReplay dropped it, and the collection was read
    // twice on every visit to this screen.
    const ownReservations$ = this.authService.user$.pipe(
      switchMap((user) =>
        // One live listener on the caller's own reservation mirrors, joined in memory,
        // instead of a get per class on every emission. The dateEpoch bound keeps the
        // read set to upcoming reservations only: mirrors of past classes (and legacy
        // mirrors without the field) are never read. Anonymous users skip it.
        user
          ? collectionData$<IUserClass>(
              fsQuery(
                collection(this.db, `users/${user.id}/classes`),
                where('dateEpoch', '>', Date.now()),
              ),
              this.zone,
            )
          : of([] as IUserClass[]),
      ),
    );

    return combineLatest([
      this.getClasses(),
      ownReservations$,
      this.ownReservationOverrides,
    ]).pipe(
      map(([classes, ownReservations, overrides]) => {
        const reservationsByClassId = new Map(
          ownReservations.map((r) => [r.classId, r.reservations ?? 0]),
        );
        const settled: string[] = [];
        const rows = classes.map<IClassWithCurrUserResModel>((classData) => {
          const serverOwn = reservationsByClassId.get(classData.id) ?? 0;
          const expected = overrides.get(classData.id);
          let currentReservationsForUser = serverOwn;
          let currentReservations = classData.currentReservations;
          if (expected !== undefined) {
            if (serverOwn === expected) {
              settled.push(classData.id);
            } else {
              // Layer only this member's not-yet-visible delta on the live total, so
              // another member's booking landing in between still shows through.
              currentReservationsForUser = expected;
              currentReservations = Math.max(
                0,
                classData.currentReservations + (expected - serverOwn),
              );
            }
          }
          return {
            id: classData.id,
            type: classData.type,
            dateEpoch: classData.dateEpoch,
            currentReservations,
            maxReservations: classData.maxReservations,
            currentReservationsForUser,
          };
        });
        if (settled.length) {
          // The server caught up; drop these on a microtask rather than re-entering
          // the stream from inside its own map.
          queueMicrotask(() => {
            const next = new Map(this.ownReservationOverrides.value);
            let changed = false;
            for (const id of settled) {
              changed = next.delete(id) || changed;
            }
            if (changed) {
              this.ownReservationOverrides.next(next);
            }
          });
        }
        return rows;
      }),
      resettableReplay<IClassWithCurrUserResModel[]>(),
    );
  }
}
